import { useEffect, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { MdOutlineClear, MdFilterList } from "react-icons/md";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";

interface SearchComponentProps {
  onSearch: (term: string) => void;
  autoComplete?: string;
  placeHolder?: string;
  isSearchTermCached?: string;
  showFilter?: boolean;
  filterOptions?: string[];
  onFilterChange?: (filter: string) => void;
  selectedFilter?: string;
}

export default function SearchComponent({
  onSearch,
  autoComplete = "on",
  placeHolder = 'Search any field below',
  isSearchTermCached = '',
  showFilter = false,
  filterOptions = [],
  onFilterChange = null,
  selectedFilter = ''
}: SearchComponentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setSearchTerm(value);
  }

  function resetSearch() {
    setSearchTerm("");
    onSearch("");
  }

  function handleFilterSelect(filter: string) {
    if (onFilterChange) {
      onFilterChange(filter);
    }
    setIsFilterOpen(false);
  }

  useDebouncedEffect(() => {
    onSearch(searchTerm.trim());
  }, [searchTerm], 300);

  useEffect(() => {
    if (isSearchTermCached) {
      setSearchTerm(isSearchTermCached);
    }
  }, [isSearchTermCached]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsFilterOpen(false);
      }
    }

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isFilterOpen]);

  return (
    <div className="flex border rounded-lg my-2">
      <div className="border-r rounded-r-lg dark:border-gray-700">
        <span className="flex px-5 text-sm font-medium items-center bg-gray-50 py-3 dark:bg-gray-900 dark:text-gray-200">
          <FaSearch />
          {!searchTerm && <span className="ml-2">Search</span>}
        </span>
      </div>

      <div className="flex justify-end relative w-full">
        <input
          value={searchTerm}
          className='border px-2 h-full w-full'
          id="search"
          onChange={handleSearchChange}
          type="text"
          placeholder={placeHolder}
          autoComplete={autoComplete}
          aria-label="Search input"
        />

        {showFilter && filterOptions.length > 0 && (
          <div ref={filterRef} className="relative border-l bg-gray-50 dark:bg-gray-900">
            <button
              type="button"
              className="px-3 py-3 flex items-center gap-1"
              title="Filter"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              aria-expanded={isFilterOpen}
              aria-haspopup="listbox"
            >
              <MdFilterList className={selectedFilter ? "text-orange-1000" : ""} />
              {selectedFilter && <span className="text-xs font-medium text-orange-1000 uppercase">({selectedFilter})</span>}
            </button>

            {isFilterOpen && (
              <div
                className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border rounded-lg shadow-lg z-50 w-64 max-h-60 overflow-y-auto transition-opacity duration-200"
                role="listbox">
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-b"
                  onClick={() => handleFilterSelect('')}
                  role="option">
                  <span className="font-medium uppercase">All Items</span>
                </button>
                {filterOptions.map((filter, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${selectedFilter === filter ? 'bg-orange-100 dark:bg-orange-900 font-medium' : ''
                      }`}
                    onClick={() => handleFilterSelect(filter)}
                    role="option"
                    aria-selected={selectedFilter === filter}>
                    <span className="uppercase">{filter}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="border-l bg-gray-50 dark:bg-gray-900">
          <button
            type="button"
            className="py-3 px-2"
            title="Clear"
            onClick={resetSearch}
            hidden={searchTerm === ""}
            aria-label="Clear search">
            <MdOutlineClear />
          </button>
        </div>
      </div>
    </div>
  );
}